import { useApolloClient, useQuery } from '@apollo/client';
import {
  compact,
  find,
  isArray,
  isFunction,
  isNil,
  isObject,
  keys,
  pick,
  property,
  some,
  toPairs,
} from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { ADDRESS_BOOK_ENTRIES_QUERY } from '../../app/graphql/addressBookQueries';
import {
  ADDRESS_AUTOCOMPLETE_DETAIL_QUERY,
  ADDRESS_AUTOCOMPLETE_QUERY,
} from '../../app/graphql/geographicQueries';
import { formatLocationAutocompleteLabel } from '../../common/utils/formatUtils';
import { omitByDeep } from '../../common/utils/funcUtils';
import { extractGraphqlEntity } from '../../common/utils/graphqlUtils';
import { useDebouncedVariable } from '../../common/utils/hookUtils';
import { sanitizeFormValueToAscii } from '../../common/utils/stringUtils';
import { useFlashMessageContext } from '../../components/dialogs/FlashMessageProvider';
import { FormItemCustomGroupAutocomplete } from '../../components/forms/CustomGroupAutocomplete';
import { useDynamicOriginalValueDependency } from '../../components/forms/dynamic/dynamicFormDependencies';
import { DynamicFormSchemaContext } from '../../components/forms/dynamic/dynamicFormSchema';
import {
  isFormSectionNonEmpty,
  isFormValuePresent,
  mergeFormNames,
} from '../../components/forms/formHelpers';
import { makeStaticCustomRule } from '../../components/forms/formValidationRules';
import { useFormContext } from '../../components/forms/forms';
import { useLabelFilter } from '../../components/forms/selectDecorators';
import { useStateIfMounted } from '../../utils/reactUtils';
import { graphQLToFormJobLocation } from '../../app/data/locationConversions';

function formValueIsPresentAndDoesntMatch({ formValue, entryValue }) {
  return isFormValuePresent(formValue) && formValue !== entryValue;
}

function getAllAddressBookEntryTexts(entry) {
  return [
    entry.companyName,
    entry.contact?.name,
    entry.contact?.email,
    entry.contact?.phone,
    entry.contact?.phoneExtension,
    entry.address?.addressLine1,
    entry.address?.addressLine2,
    entry.address?.addressLine3,
    entry.address?.city?.text,
    entry.address?.stateProvince?.text,
    entry.address?.postalCode,
    entry.address?.country?.text,
    entry.specialInstructions,
  ];
}

function useCountryCode({ countrySchemaName }) {
  const countryText = useDynamicOriginalValueDependency(countrySchemaName);
  const { optionsQuery } = useContext(DynamicFormSchemaContext);
  const { data } = useQuery(optionsQuery, {
    skip: !countrySchemaName || !countryText,
    variables: { input: { fieldName: countrySchemaName, value: countryText } },
  });
  const countryOptions = data && extractGraphqlEntity(data)?.values;

  if (countryOptions?.length === 1) {
    return countryOptions[0].code;
  }
  if (countryOptions?.length > 1) {
    return find(countryOptions, opt => opt.text === countryText)?.code;
  }
  return null;
}

function useFilterAddressBookOption({ filterOption, searchText }) {
  // Address book entry should be included in the results if:
  // - all of country, state and city are either not filled or match the entry
  // - the focused field is a substring of entry's field
  return useCallback(
    (address, { entry }) => {
      const filteredFields = [
        {
          formValue: address?.country,
          entryValue: entry?.address?.country?.text,
        },
        {
          formValue: address?.stateProvince,
          entryValue: entry?.address?.stateProvince?.text,
        },
        {
          formValue: address?.city,
          entryValue: entry?.address?.city?.text,
        },
      ];
      if (some(filteredFields, formValueIsPresentAndDoesntMatch)) {
        return false;
      }

      return some(getAllAddressBookEntryTexts(entry), sourceText =>
        filterOption(searchText, { label: sourceText })
      );
    },
    [filterOption, searchText]
  );
}

function useFilteredAddressBookItems({ searchText, countryCodeRestrict }) {
  const accountNumber = useDynamicOriginalValueDependency('accountNumber');
  const { values } = useFormContext();

  const { filterOption } = useLabelFilter();
  const filterAddressBookOption = useFilterAddressBookOption({
    filterOption,
    searchText,
  });

  const { data, loading, error } = useQuery(ADDRESS_BOOK_ENTRIES_QUERY, {
    skip: !accountNumber,
    variables: { accountNumber, countryCode: countryCodeRestrict },
  });

  const allOptions = useMemo(() => {
    const entries = data ? extractGraphqlEntity(data).data : [];

    return entries.map(entry => ({
      entry,
      value: entry.id,
      label: formatLocationAutocompleteLabel(entry),
    }));
  }, [data]);

  const options = useMemo(
    () =>
      allOptions.filter(opt => filterAddressBookOption(values?.address, opt)),
    [allOptions, values?.address, filterAddressBookOption]
  );

  if (loading) {
    return { loading: true };
  }
  if (error) {
    return { error };
  }

  return { options };
}

function useWebAddressItems({ searchText, countrySchemaName, skip }) {
  const [sessionToken, setSessionToken] = useStateIfMounted(() => uuidv4());
  const resetSessionToken = useCallback(() => {
    setSessionToken(uuidv4());
  }, [setSessionToken]);

  const countryCode = useCountryCode({ countrySchemaName });

  const { errorMessage } = useFlashMessageContext();
  const client = useApolloClient();
  const loadDetail = useCallback(
    async ({ id }) => {
      try {
        const { data } = await client.query({
          query: ADDRESS_AUTOCOMPLETE_DETAIL_QUERY,
          variables: { input: { id, sessionToken } },
        });
        return extractGraphqlEntity(data);
      } catch (e) {
        errorMessage(e);
        throw e;
      }
    },
    [client, errorMessage, sessionToken]
  );

  const [lastData, setLastData] = useState();
  const { data, loading, error } = useQuery(ADDRESS_AUTOCOMPLETE_QUERY, {
    skip: !searchText || skip,
    variables: { input: { searchText, sessionToken, countryCode } },
    onCompleted: dt => {
      setLastData(dt);
    },
  });
  if (loading && !lastData) {
    return { loading: true };
  }
  if (error) {
    return { error };
  }

  const usedData = data || lastData;
  const entries = usedData ? extractGraphqlEntity(usedData).data : [];
  const options = entries.map(entry => ({
    entry,
    value: entry.id,
    label: entry.text,
  }));

  return { options, resetSessionToken, loadDetail };
}

const CUSTOM_ADDRESS_ID = uuidv4();

function AddressAutoComplete({
  onSelect: onSelectOuter,
  countrySchemaName,
  enableWebResults = true,
  enableAddressBookResults = true,
  namePrefix,
  countryCodeRestrict,
}) {
  const intl = useIntl();
  const {
    formInstance,
    forceUpdate,
    submittingRef,
    values: formValues,
  } = useFormContext();

  const formVal = namePrefix
    ? formValues[namePrefix]?.addressSearchText
    : formValues.addressSearchText;
  const searchText = useDebouncedVariable(formVal, {
    delay: 250,
  });
  const addressBookGroup = useFilteredAddressBookItems({
    searchText,
    countryCodeRestrict,
  });
  const {
    resetSessionToken,
    loadDetail: loadWebAddressDetail,
    ...webAddressGroup
  } = useWebAddressItems({
    searchText,
    countrySchemaName,
    skip: !enableWebResults,
  });

  const options = compact([
    {
      groupId: 'custom',
      options: [
        {
          value: CUSTOM_ADDRESS_ID,
          label: intl.formatMessage({
            id: 'address.autoComplete.customAddress',
          }),
        },
      ],
    },
    enableAddressBookResults && {
      groupId: 'addressBook',
      headerId: 'address.autoComplete.addressBook',
      ...addressBookGroup,
    },
    enableWebResults && {
      groupId: 'web',
      headerId: 'address.autoComplete.web',
      ...webAddressGroup,
    },
  ]);

  const onSelect = async opt => {
    try {
      onSelectOuter && onSelectOuter(opt);

      if (opt.value === CUSTOM_ADDRESS_ID) {
        return;
      }

      const values = graphQLToFormJobLocation(
        opt.entry?.text ? await loadWebAddressDetail(opt.entry) : opt.entry
      );
      if (namePrefix)
        formInstance.setFieldsValue({
          [namePrefix]: sanitizeFormValueToAscii(values),
        });
      else formInstance.setFieldsValue(sanitizeFormValueToAscii(values));
      forceUpdate();
    } finally {
      resetSessionToken();
    }
  };

  return (
    <FormItemCustomGroupAutocomplete
      name={mergeFormNames(namePrefix, 'addressSearchText')}
      placeholder={
        // We want it to look like required but to have a custom validation only run at submit time
        `${intl.formatMessage({
          id: 'address.lookup.placeholder',
        })} *`
      }
      floatingLabel={false}
      options={options}
      formItemComponentProps={{
        onSelect,
        setLabelOnSelect: false,
      }}
      rules={[
        // Only show error when submitting
        makeStaticCustomRule(
          'error.addressRequired',
          () => !submittingRef.current
        ),
      ]}
    />
  );
}

function getAutocompleteableLocationPart(values) {
  return omitByDeep(
    pick(values, ['address', 'contact', 'companyName']),
    (val, key) => key === 'country'
  );
}

function getFieldNames(values, depth = 0, prefix = []) {
  if (depth === 0) {
    return keys(values).map(key => ({
      fieldName: [...prefix, key],
    }));
  }
  return toPairs(values)
    .map(([key, val]) => {
      if (isNil(val)) {
        return null;
      }
      if (isObject(val)) {
        return getFieldNames(val, depth - 1, [...prefix, key]);
      }
      return { fieldName: [...prefix, key] };
    })
    .reduce((prev, cur) => {
      if (isNil(cur)) {
        return prev;
      }
      if (isArray(cur)) {
        return [...prev, ...cur];
      }
      return [...prev, cur];
    }, []);
}

export default function AddressLookup({
  children,
  renderLookup,
  countrySchemaName,
  enableWebResults = true,
  enableAddressBookResults = true,
  namePrefix,
  countryCodeRestrict,
}) {
  const { values, formInstance, forceUpdate } = useFormContext();
  const [showLookup, setShowLookup] = useState(true);

  const clearFormValue = useCallback(() => {
    if (namePrefix) {
      formInstance.setFieldsValue({ [namePrefix]: { addressSearchText: '' } });
    } else {
      formInstance.setFieldsValue({ addressSearchText: '' });
    }
  }, [formInstance, namePrefix]);

  const onSelect = useCallback(
    opt => {
      setShowLookup(false);
      clearFormValue();
    },
    [clearFormValue]
  );
  const onReset = useCallback(() => {
    setShowLookup(true);
    clearFormValue();

    if (namePrefix) {
      const formVals = formInstance.getFieldsValue();
      const resettable = getAutocompleteableLocationPart(formVals[namePrefix]);
      const resettableFields = getFieldNames(resettable, 1);
      formInstance.resetFields(
        resettableFields.map(p => [namePrefix, ...property('fieldName')(p)])
      );
    } else {
      const resettable = getAutocompleteableLocationPart(
        formInstance.getFieldsValue()
      );
      const resettableFields = getFieldNames(resettable, 1);
      formInstance.resetFields(resettableFields.map(property('fieldName')));
    }
    forceUpdate();
  }, [forceUpdate, formInstance, clearFormValue, namePrefix]);

  const addressNonEmpty = isFormSectionNonEmpty(
    getAutocompleteableLocationPart(namePrefix ? values[namePrefix] : values)
  );
  // As soon as address is non empty, lookup will be disabled
  useEffect(() => {
    if (addressNonEmpty) {
      setShowLookup(false);
    }
  }, [addressNonEmpty]);

  const field = (
    <AddressAutoComplete
      onSelect={onSelect}
      countrySchemaName={countrySchemaName}
      enableAddressBookResults={enableAddressBookResults}
      enableWebResults={enableWebResults}
      namePrefix={namePrefix}
      countryCodeRestrict={countryCodeRestrict}
    />
  );

  if (showLookup) {
    return renderLookup(field);
  }

  return isFunction(children) ? children({ onReset }) : children;
}
